
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface StatusHistoryEntry {
  id: string;
  document_id: string;
  previous_status: string | null;
  new_status: string;
  changed_by: string;
  changed_at: string;
  notes: string | null;
  user_details?: {
    full_name?: string;
    email?: string;
  };
}

export function useDocumentStatusHistory(documentId?: string) {
  const [isLoading, setIsLoading] = useState(false);

  const fetchStatusHistory = async () => {
    if (!documentId) return [];
    
    try {
      setIsLoading(true);
      
      // Fetch status history for the document
      const { data: history, error } = await supabase
        .from('document_status_history')
        .select('*')
        .eq('document_id', documentId)
        .order('changed_at', { ascending: false });
      
      if (error) throw error;
      
      // Get user details for each entry
      const historyWithUserDetails = await Promise.all(
        history.map(async (entry) => {
          const { data: userData, error: userError } = await supabase
            .from('profiles')
            .select('full_name, email')
            .eq('id', entry.changed_by)
            .single();
          
          return {
            ...entry,
            user_details: userError ? undefined : userData
          };
        })
      );
      
      return historyWithUserDetails as StatusHistoryEntry[];
    } catch (err) {
      console.error('Error fetching status history:', err);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  return {
    fetchStatusHistory,
    isLoading
  };
}
